import { type DateFormat, Format, dateFns } from '@/lib/dateFns';
import { newDate } from '@/utils/date';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * 汎用日時フォーマッター
 * @param value
 * @returns
 */
export const formatDateCommon = (value: string | Date, format: DateFormat, isJST = false) => {
  if (!value) return '';
  if (typeof value === 'string') {
    return isJST ? formatInTimeZone(newDate(value), 'Asia/Tokyo', format) : dateFns.format(newDate(value), format);
  }
  return isJST ? formatInTimeZone(value, 'Asia/Tokyo', format) : dateFns.format(value, format);
};

/**
 * 日時フォーマッター
 * @param value
 * @returns
 */
export const formatDateTime = (value: string | Date) => {
  if (!value) return '';
  if (typeof value === 'string') {
    return dateFns.format(newDate(value), Format.dateTime);
  }
  return dateFns.format(value, Format.dateTime);
};

/**
 * 日時フォーマッター
 * mm月上旬 || 中旬 || 下旬の表記
 * @param value
 * @returns
 */
export const formatDateAboutJa = (value: string | Date) => {
  if (!value) return '';

  let targetDate: Date;
  if (typeof value === 'string') {
    targetDate = newDate(value);
  } else {
    targetDate = value;
  }

  const month = targetDate.getMonth() + 1; //date-fnsでは月は0始まり
  const baseText = `${month}月`;
  const date = targetDate.getDate();
  if (date >= 1 && date < 11) {
    return `${baseText}上旬`;
  }
  if (date >= 11 && date < 21) {
    return `${baseText}中旬`;
  }
  return `${baseText}下旬`;
};

/**
 * 数値フォーマッター（金額など）
 * @param value
 * @param blank2Zero
 * @returns
 */
export const formatCurrency = (value: string | number, blank2Zero?: boolean) => {
  if (value == null) {
    return blank2Zero ? '0' : '';
  }
  if (typeof value === 'string') {
    return Number.parseInt(value, 10).toLocaleString();
  }
  return value.toLocaleString();
};

/**
 * 住所フォーマッター
 * @param value
 * @returns
 */
export const formatPostCode = (value: string | number) => {
  if (!value) return '';

  // 数字以外の文字を除去
  const cleanZip = value.toString().replace(/[^\d]/g, '');

  // 7桁あれば3-4で分割
  if (cleanZip.length === 7) {
    return `${cleanZip.slice(0, 3)}-${cleanZip.slice(3)}`;
  }
  // 形式が合わない場合はそのまま返す
  return value;
};

/**
 * 数値を指定した小数点桁数で切り捨てする
 * @param value 対象の数値
 * @param decimalPlaces 残す小数点桁数（デフォルト: 6）
 * @returns 切り捨て処理された数値
 */
export const truncateDecimals = (value: number, decimalPlaces = 6): number => {
  const multiplier = 10 ** decimalPlaces;
  return Math.floor(value * multiplier) / multiplier;
};

/**
 * 配列の要素から指定したプロパティの値を取得し、表示用文字列に変換する共通関数
 * - 要素が複数ある場合: `/` で連結する
 * - 要素が1つの場合: そのまま返す
 * @param items オブジェクトの配列
 * @param getValue 各要素から値を取得する関数
 * @returns 表示用の文字列
 */
const formatArrayItems = <T>(items: T[] | null | undefined, getValue: (item: T) => string): string => {
  if (!items || items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return getValue(items[0]);
  }
  return items.map(getValue).join(' / ');
};

/**
 * 作家名を表示用文字列に変換する
 * - 作家が複数ある場合: `/` で連結する
 * - 作家が1つの場合: そのまま返す
 * @param creators 作家の配列（nameプロパティを持つオブジェクトの配列）
 * @returns 表示用の文字列
 */
export const formatCreatorNames = (creators: Array<{ name: string }>): string => {
  return formatArrayItems(creators, (creator) => creator.name);
};

/**
 * // TODO(GF-1743): API移行完了後に削除
 * コピーライトテキストを表示用文字列に変換する
 * - コピーライトが複数ある場合: `/` で連結する
 * - コピーライトが1つの場合: そのまま返す
 * @param authors 作家の配列（copyrightShortプロパティを持つオブジェクトの配列）
 * @returns 表示用の文字列（© マークを含む）
 */
export const formatCopyrightTexts = (authors: Array<{ copyrightShort: string | null | undefined }>): string => {
  return authors
    .map((author) => author.copyrightShort)
    .filter((text): text is string => Boolean(text))
    .map((text) => `© ${text}`)
    .join(' / ');
};

/**
 * 作品名の長さに応じてフォントサイズ種別を返す
 * @param workName - 作品名
 * @returns フォントサイズ種別（'small' | 'medium' | 'default'）
 */
export const getWorkNameFontSize = (workName: string): 'small' | 'medium' | 'default' => {
  const length = workName.length;

  if (length > 20) {
    // 20文字以上は小さいフォントサイズ
    return 'small';
  }
  if (length > 16) {
    // 16-20文字は中サイズのフォントサイズ
    return 'medium';
  }
  // 16文字以下は通常のフォントサイズ
  return 'default';
};
